import { get } from 'lodash';
import { add, subMonths, lastDayOfMonth, startOfMonth } from 'date-fns';

import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import {
  AggregateMetricsData,
  FrequencyManagementFilters,
  MessagingInsightsQuery,
} from 'modules/frequencyManagement/components/engagementMetrics/types';
import { MessageMedium } from 'constants/messageMediums';
import { DateInput, parseDate, guessCurrentTimeZone } from 'utils/datetime';
import { CampaignFiltersKeys } from 'modules/messagingInsights/types';
import { localizePercent } from 'utils/l10n/percent';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import { CampaignToHighLevelMetric } from 'modules/messagingInsights/constants';

/**
  @description Engagement metric display copies by medium
*/
export const getMetricsDisplayByMedium = (medium: SupportedMediums) => {
  const metrics = {
    [MessageMedium.Email]: ['Sends per user', 'Open rate', 'Click rate', 'Unsubscribe rate'],
    [MessageMedium.Push]: ['Pushes per user', 'Open rate', 'Unsubscribe rate'],
    [MessageMedium.SMS]: ['Sends per user', 'Click rate', 'Unsubscribe rate'],
  };
  return metrics[medium];
};

/**
  @description Engagement metric labels by medium. Can be mapped to METRIC_VALUES_TO_LABELS

*/
export const presetMetricsByMedium = {
  [MessageMedium.Email]: [
    'TotalEmailSend',
    'UniqueEmailSend',
    'EmailOpenRate',
    'EmailClickRate',
    'EmailUnsubscribeRate',
  ],
  [MessageMedium.Push]: ['TotalPushSend', 'UniquePushSend', 'PushOpenRate', 'PushUnsubscribeRate'],
  [MessageMedium.SMS]: ['TotalSMSSend', 'UniqueSMSDelivered', 'SMSClickRate', 'SMSUnsubscribeRate'],
};

/**
 @description Creates the start and end times for the previous month 
 */
export const getPreviousMonthRange = (newDate?: DateInput) => {
  const currentDate = parseDate(newDate);
  const lastMonth = subMonths(currentDate, 1);
  return {
    startTime: startOfMonth(lastMonth),
    endTime: add(lastDayOfMonth(lastMonth), { hours: 23, minutes: 59, seconds: 59 }),
  };
};

/**
 @description Creates the request query body for the engagement metrics API request
 */
export const createMetricsQueryBody = (
  messageMedium: SupportedMediums,
  messageTypeId?: number | null,
) => {
  return {
    ...getPreviousMonthRange(),
    campaignFilters: {
      mediums: [messageMedium],
      ...(messageTypeId ? { messageTypeIds: [messageTypeId] } : {}),
    },
    metrics: {
      overallMetrics: [],
      mediumMetrics: presetMetricsByMedium[messageMedium].map((metric) => ({ name: metric })),
    },
  };
};

/**
 @description Parses engagement metrics data and performs rounding logic for display 
 */
export const parseMetricsData = (
  data: AggregateMetricsData | undefined,
  messageMedium: SupportedMediums,
) => {
  if (!data?.mediumMetrics) {
    return [];
  }

  // Parse sends per user
  const presetMetrics = presetMetricsByMedium[messageMedium];
  const totalSendsMetric = METRIC_VALUES_TO_LABELS[presetMetrics[0]];
  const uniqueSendsMetric = METRIC_VALUES_TO_LABELS[presetMetrics[1]];
  const sendsPerUser =
    get(data.mediumMetrics, [totalSendsMetric, 'value'], 0) /
    get(data.mediumMetrics, [uniqueSendsMetric, 'value'], 0);
  const formattedSendsPerUser = Number.isFinite(sendsPerUser) ? Math.round(sendsPerUser) : 0;

  const remainingMetricValues = presetMetrics.slice(2).map((metricName) => {
    return localizePercent(
      get(data.mediumMetrics, [METRIC_VALUES_TO_LABELS[metricName], 'value'], 0),
    );
  });

  return [`${formattedSendsPerUser}`, ...remainingMetricValues];
};

/**
 @description Transforms parameters to messaging insights filters
 */
export const transformToMessagingInsightsFilters = (
  messageMedium: SupportedMediums,
  startTime: Date,
  endTime: Date,
  messageTypeIds: number[],
  timezone?: string,
): FrequencyManagementFilters => {
  return {
    selectedMediums: [messageMedium],
    selectedCrossMetrics: presetMetricsByMedium[messageMedium].map(
      (metric) => (CampaignToHighLevelMetric as Record<string, string>)[metric] || metric,
    ),
    campaignFilters: {
      messageTypeIds,
    },
    dateParams: {
      startTime,
      endTime,
    },
    timezone: timezone || '',
  };
};

/**
 @description Creates the request query body and url for messaging insights 
*/
export const createMessagingInsightsUrlQuery = (allFrMFilters: FrequencyManagementFilters) => {
  const { selectedMediums, selectedCrossMetrics, campaignFilters, dateParams, timezone } =
    allFrMFilters;
  const queryParams = {} as MessagingInsightsQuery;

  queryParams.messageMediums = selectedMediums.join(',');
  queryParams.metrics = selectedCrossMetrics.join(',');

  let parsedTimeZone = timezone;
  if (!parsedTimeZone) {
    parsedTimeZone = guessCurrentTimeZone();
  }
  queryParams.timezone = parsedTimeZone;
  const { startTime, endTime } = dateParams;

  queryParams.startTime = startTime?.toISOString() || '';
  queryParams.endTime = endTime?.toISOString() || '';

  const { messageTypeIds } = campaignFilters;
  queryParams[CampaignFiltersKeys.MESSAGE_TYPE_IDS] = (messageTypeIds as number[]).join(',');
  const baseUrl = '/messagingInsights?';
  const urlParams = new URLSearchParams(queryParams);

  return {
    queryParams,
    finalUrl: `${baseUrl}${urlParams.toString()}`,
  };
};
